// Receiving side of the cross-app impersonation hand-off.
//
// The admin app (mgmt.staging.dosen.io) sets a parent-domain cookie
// (`dosen_imp`, Domain=.dosen.io) carrying a one-time impersonation token, plus
// `dosen_imp_name` with the target's display name. This app reads the token and
// stamps it as `X-Impersonation-Token` on its V4 calls; the backend's
// requireUser then swaps the acting identity to the target. See
// dosen-user-web/src/lib/impersonation-cookie.ts for the writer.
//
// The token only works when THIS app is authenticated as the same platform
// admin who started the session (backend constraint): SSO across the shared
// tenant provides exactly that. Read live from the cookie on every request so
// the admin setting/clearing it (or its Max-Age lapsing) takes effect at once.

const TOKEN_COOKIE = 'dosen_imp';
const NAME_COOKIE = 'dosen_imp_name';
// Admin app origin — where "Return to admin" sends the admin. Reloading THIS app
// as the real (platform-admin) identity trips its end-user guards into a full
// Auth0 logout, so exiting navigates back to the admin console instead.
const FROM_COOKIE = 'dosen_imp_from';

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

/** The impersonation token to forward, or null when not impersonating. */
export const getHandoffToken = (): string | null => readCookie(TOKEN_COOKIE);

/** Target display name for the banner, or null. */
export const getHandoffName = (): string | null => readCookie(NAME_COOKIE);

/** Admin app origin to return to on exit, or null. */
export const getHandoffOrigin = (): string | null => readCookie(FROM_COOKIE);

// Where to send a platform admin who has no end-user home here: the origin the
// hand-off recorded, else the build-time admin URL. Empty string when neither is
// set (dev) — callers then no-op rather than navigate nowhere.
export const adminUrl = (): string => getHandoffOrigin() || process.env.REACT_APP_ADMIN_URL || '';

// Identity that belongs to whoever the app last bootstrapped as. These are read
// synchronously by route guards and by /users/:id and /orgs/:orgId calls, so a
// value left over from a PREVIOUS impersonation target makes the next session
// query the wrong user/org → 403/404 and a bogus "not authorized" page. Cleared
// whenever the impersonation token changes (see reconcileImpersonationIdentity).
const IDENTITY_KEYS = [
  'role',
  'v4UserId',
  'v4OrgId',
  'orgLocation',
  'username',
  'isAuthenticated',
  'lastseen',
  'persist:root-v4'
];
const SESSION_MARKER = 'dosen.imp.session';
// Set while a fresh impersonation identity is being bootstrapped (token changed,
// old identity wiped, getToken not yet done). AppRoutes holds the whole render
// on this so no route/guard/data-hook runs with a half-populated identity.
const RESOLVING_FLAG = 'dosen.imp.resolving';

// How long the render gate may hold before it auto-releases. A safety net: if
// getToken never clears the flag (e.g. it threw before confirming the target),
// the app must not stay blank forever — it releases and renders whatever state
// it has rather than a permanent white page.
const RESOLVING_MAX_MS = 8000;

// Runs BEFORE the app mounts. Only a token CHANGE (a new hand-off) needs the
// gate: it wipes the previous target's identity so the fresh bootstrap can't
// inherit its user/org ids (→ 403/404), then flags "resolving" so the app holds
// until getToken lands the new target's role/ids. A plain refresh keeps its
// (correct) identity and sets no gate. No token → impersonation ended, clear
// everything. The flag's value is a timestamp for the auto-release above.
export const reconcileImpersonationIdentity = (): void => {
  try {
    const token = getHandoffToken() || '';
    const marker = localStorage.getItem(SESSION_MARKER) || '';
    if (!token) {
      if (marker) for (const key of IDENTITY_KEYS) localStorage.removeItem(key);
      localStorage.removeItem(SESSION_MARKER);
      localStorage.removeItem(RESOLVING_FLAG);
      return;
    }
    if (token !== marker) {
      for (const key of IDENTITY_KEYS) localStorage.removeItem(key);
      localStorage.setItem(SESSION_MARKER, token);
      localStorage.setItem(RESOLVING_FLAG, String(Date.now()));
    }
  } catch {
    /* localStorage unavailable — nothing to reconcile */
  }
};

/** True while a new impersonation identity is still bootstrapping (bounded). */
export const isImpersonationResolving = (): boolean => {
  try {
    const raw = localStorage.getItem(RESOLVING_FLAG);
    if (!raw) return false;
    const started = Number(raw);
    if (!Number.isFinite(started) || Date.now() - started > RESOLVING_MAX_MS) {
      localStorage.removeItem(RESOLVING_FLAG);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/** Called once getToken has populated the target's role + ids. */
export const clearImpersonationResolving = (): void => {
  try {
    localStorage.removeItem(RESOLVING_FLAG);
  } catch {
    /* ignore */
  }
};

/** True while a hand-off session is present. */
export const isImpersonating = (): boolean => getHandoffToken() !== null;

// Clears the cookie the admin set. Must repeat the SAME domain used to write it
// (.dosen.io) — a domain-less expire leaves a parent-domain cookie in place.
// Mirrors the host-only vs parent-domain rule in regionBootstrap's clearCookie.
export const clearHandoff = (): void => {
  const domain = window.location.hostname.endsWith('dosen.io') ? '; domain=.dosen.io' : '';
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  for (const name of [TOKEN_COOKIE, NAME_COOKIE, FROM_COOKIE]) {
    document.cookie = `${name}=; path=/${domain}; max-age=0; samesite=lax${secure}`;
  }
};
